/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import axios from 'axios';

import {
    URLHelper,
    Alert,
    Button,
    ButtonGroup,
    Checkbox,
    FauxImage,
    FlexCol,
    FlexRow,
    Heading,
    Icon,
    StyledModal,
    ModalActions,
    Spinner,
    Text,
    TextField,
    useToast,
    Divider,
} from 'sarsaparilla';
import { getAuthHeader } from 'sarsaparilla/auth';

import { FavoriteHeartRemoveAllModal } from './FavoriteHeartRemoveAllModal';

interface FavoriteHeartAddRemoveModalProps {
    // action being taken on favorite
    mode: 'add' | 'edit';

    // entity id
    entityId: string;

    // example: campsite, sitepass, recarea
    entityType: string;

    // parent id
    parentId?: string;

    // parent type
    parentType?: string;

    // modal save action
    onSave?: () => void;

    // modal cancel action
    onCancel?: () => void;
}
// individual favorite item cards
type FavoriteGroupItem = {
    entity_id?: string;
    entity_type?: string;
    parent_id?: string;
    parent_type?: string;
    is_active?: boolean;
    account_id?: string;
    created_date?: string;
    updated_date?: string;
    group_id?: string;
};

// category group filled with item cards
type FavoriteGroup = {
    favorites: FavoriteGroupItem[];
    group_id: string;
    group_name: string;
    account_id: string;
    is_active?: boolean;
    is_primary?: boolean;
    created_date?: string;
    updated_date?: string;
};

// detailed result of favorite item from api call
type FavoriteEntityDetails = {
    aggregate_cell_coverage?: number;
    average_rating?: number;
    campsite_type_of_use?: string[];
    city?: string;
    country_code?: string;
    description?: string;
    entity_id?: string;
    entity_type?: string;
    go_live_date?: string;
    campsite_id?: string;
    id?: string;
    latitude?: string;
    longitude?: string;
    name?: string;
    number_of_ratings?: number;
    org_id?: string;
    org_name?: string;
    parent_id?: string;
    parent_name?: string;
    preview_image_url?: string;
    reservable?: boolean;
    state_code?: string;
    tours_count?: string;
};

function cloneFavoriteGroupsForSave(groups: FavoriteGroup[]): FavoriteGroup[] {
    return groups.map((g) => ({
        ...g,
        favorites: (g.favorites || []).map((f) => ({ ...f })),
    }));
}

// Buckets keep GA event cardinality bounded instead of emitting a unique value per count.
function bucketExistingListsCount(count: number): '1-2' | '3-4' | '5-10' | '11+' {
    if (count <= 2) return '1-2';
    if (count <= 4) return '3-4';
    if (count <= 10) return '5-10';
    return '11+';
}

function existingListsPhrase(count: number): string {
    return `${bucketExistingListsCount(count)} existing lists`;
}

function removalPartsForGa(
    existingCustomRemoves: number,
    allFavoritesRemoved: boolean
): string[] {
    const parts: string[] = [];
    if (existingCustomRemoves > 0) {
        parts.push(existingListsPhrase(existingCustomRemoves));
    }
    if (allFavoritesRemoved) {
        parts.push('All Favorites');
    }
    return parts;
}

function buildFavoritePayloadItem(
    entityId: string,
    entityType: string,
    parentId?: string,
    parentType?: string
): FavoriteGroupItem {
    return {
        entity_id: entityId,
        entity_type: entityType,
        parent_id: parentId,
        parent_type: parentType,
    };
}

type ComputeFavoritesSaveInput = {
    groups: FavoriteGroup[];
    selectedGroups: { [key: string]: boolean };
    newGroupName: string | undefined;
    entityId: string;
    entityType: string;
    parentId?: string;
    parentType?: string;
};

/**
 * Pure: builds the API payload and a cloned groups snapshot. Callers apply `setGroups(groupsCopy)`.
 * A single save can include a new list and existing lists (e.g. lists stay checked when creating a new list).
 */
function computeFavoritesGroupsToSave({
    groups,
    selectedGroups,
    newGroupName,
    entityId,
    entityType,
    parentId,
    parentType,
}: ComputeFavoritesSaveInput): {
    toSave: Partial<FavoriteGroup>[];
    groupsCopy: FavoriteGroup[];
} {
    const toSave: Partial<FavoriteGroup>[] = [];
    const groupsCopy = cloneFavoriteGroupsForSave(groups);

    groupsCopy.forEach((favoriteItem) => {
        const favoriteGroup: FavoriteGroup = favoriteItem;
        const found = favoriteGroup.favorites?.filter(
            (f) => f.entity_id === entityId && f.entity_type === entityType
        );
        if (selectedGroups[favoriteGroup.group_id] === true) {
            if (!found || found.length === 0) {
                const favorites = favoriteGroup.favorites || [];
                favorites.push(
                    buildFavoritePayloadItem(entityId, entityType, parentId, parentType)
                );
                favoriteGroup.favorites = favorites;
                toSave.push(favoriteGroup);
            }
        } else if (found?.length > 0) {
            favoriteGroup.favorites = favoriteGroup.favorites?.map((fav) => {
                const f = fav;
                if (f.entity_id === entityId && f.entity_type === entityType) {
                    f.is_active = false;
                }
                return f;
            });
            toSave.push(favoriteGroup);
        }
    });

    Object.keys(selectedGroups).forEach((selectedFavorite) => {
        if (selectedFavorite === 'new' && selectedGroups[selectedFavorite]) {
            toSave.push({
                group_name: newGroupName,
                favorites: [
                    buildFavoritePayloadItem(entityId, entityType, parentId, parentType),
                ],
                is_active: true,
            });
        }
        if (selectedFavorite === 'all' && selectedGroups[selectedFavorite]) {
            const all = groupsCopy.filter((g) => g.group_name === 'All Favorites');
            if (all.length === 0) {
                toSave.push({
                    group_name: 'All Favorites',
                    favorites: [
                        buildFavoritePayloadItem(
                            entityId,
                            entityType,
                            parentId,
                            parentType
                        ),
                    ],
                    is_active: true,
                });
            } else {
                const allGroup = all[0];

                const favArray = allGroup.favorites?.filter(
                    (f) => f.entity_id === entityId && f.entity_type === entityType
                );
                if (!favArray || favArray.length === 0) {
                    const fav = allGroup.favorites || [];
                    fav.push(
                        buildFavoritePayloadItem(
                            entityId,
                            entityType,
                            parentId,
                            parentType
                        )
                    );
                    allGroup.favorites = fav;
                    toSave.push(allGroup);
                }
            }
        }
    });

    return { toSave, groupsCopy };
}

type FavoriteSaveGaFlags = {
    newCustomList: boolean;
    existingCustomAdds: number;
    existingCustomRemoves: number;
    allFavoritesOut: 'none' | 'created' | 'added' | 'removed';
};

function deriveFavoriteSaveGaFlagsFromToSave(
    toSave: Partial<FavoriteGroup>[],
    entityId: string,
    entityType: string
): FavoriteSaveGaFlags {
    let newCustomList = false;
    let existingCustomAdds = 0;
    let existingCustomRemoves = 0;
    let allFavoritesRemoved = false;
    let allFavoritesCreated = false;
    let allFavoritesAdded = false;

    for (let i = 0; i < toSave.length; i += 1) {
        const item = toSave[i];
        const name = item.group_name ?? '';
        const hasGroupId = Boolean(item.group_id);

        if (!hasGroupId) {
            if (name === 'All Favorites') {
                allFavoritesCreated = true;
            } else if (name.trim()) {
                newCustomList = true;
            }
            continue;
        }

        const isAllFavorites = name === 'All Favorites';
        const fav = item.favorites?.find(
            (f) => f.entity_id === entityId && f.entity_type === entityType
        );

        if (isAllFavorites) {
            if (fav?.is_active === false) {
                allFavoritesRemoved = true;
            } else if (fav) {
                allFavoritesAdded = true;
            }
            continue;
        }

        if (fav?.is_active === false) {
            existingCustomRemoves += 1;
        } else if (fav) {
            existingCustomAdds += 1;
        }
    }

    let allFavoritesOut: FavoriteSaveGaFlags['allFavoritesOut'] = 'none';
    if (allFavoritesRemoved) {
        allFavoritesOut = 'removed';
    } else if (allFavoritesCreated) {
        allFavoritesOut = 'created';
    } else if (allFavoritesAdded) {
        allFavoritesOut = 'added';
    }

    return {
        newCustomList,
        existingCustomAdds,
        existingCustomRemoves,
        allFavoritesOut,
    };
}

function formatFavoriteSaveGaTrackingValue(
    flags: FavoriteSaveGaFlags,
    mode: 'add' | 'edit'
): string {
    const { newCustomList, existingCustomAdds, existingCustomRemoves, allFavoritesOut } =
        flags;

    const allFavoritesPersisted =
        allFavoritesOut === 'created' || allFavoritesOut === 'added';

    const addDestinations: string[] = [];
    if (newCustomList) {
        addDestinations.push('a new list');
    }
    if (existingCustomAdds > 0) {
        addDestinations.push(existingListsPhrase(existingCustomAdds));
    }

    let sentence: string;
    if (addDestinations.length === 0) {
        if (existingCustomRemoves > 0 || allFavoritesOut === 'removed') {
            const removalParts = removalPartsForGa(
                existingCustomRemoves,
                allFavoritesOut === 'removed'
            );
            sentence = `Favorite Saved: removed from ${removalParts.join(' and ')}`;
        } else if (allFavoritesPersisted) {
            sentence = 'Favorite Saved to All Favorites';
        } else {
            sentence = 'Favorite Saved: no list changes';
        }
    } else if (addDestinations.length === 1) {
        const [only] = addDestinations;
        sentence = `Favorite Saved to ${only}`;
    } else {
        sentence = `Favorite Saved to ${addDestinations[0]} and ${addDestinations[1]}`;
    }

    let removalSuffix = '';
    // Only when there are adds + removes; removals-only already uses "removed from …" in `sentence`.
    if (
        addDestinations.length > 0 &&
        (existingCustomRemoves > 0 || allFavoritesOut === 'removed')
    ) {
        const removalBits = removalPartsForGa(
            existingCustomRemoves,
            allFavoritesOut === 'removed'
        );
        removalSuffix = `; also removed from ${removalBits.join(' and ')}`;
    }

    return `${sentence}${removalSuffix} — ${mode}`;
}

export function FavoriteHeartAddRemoveModal({
    mode,
    entityId,
    entityType,
    parentId,
    parentType,
    onSave,
    onCancel,
}: FavoriteHeartAddRemoveModalProps) {
    const [groups, setGroups] = React.useState<FavoriteGroup[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [errors, setErrors] = React.useState<string[]>([]);
    const [newGroupName, setNewGroupName] = React.useState<string>();
    const [selectedGroups, setSelectedGroups] = React.useState<{
        [key: string]: boolean;
    }>({});
    const [isOpen, setOpen] = React.useState(false);
    const [isCreateNewList, setCreateNewList] = React.useState(false);
    const [isEdited, setEdited] = React.useState(false);
    const [images, setImages] = React.useState<{ [key: string]: any }>({});
    const [favoritesDetails, setFavoritesDetails] = React.useState<Record<
        string,
        FavoriteEntityDetails
    > | null>(null);
    const [isRemoveAllModalOpen, setIsRemoveAllModalOpen] = React.useState(false);
    const showToast = useToast();

    const loadPreviewImages = async (groupItem: FavoriteGroup[]) => {
        try {
            // collect ids of all favorites and query search service for any records with preview image urls
            const docIds = new Set();

            for (let i = 0; i < groupItem.length; i += 1) {
                const group = groupItem[i];
                for (let j = 0; j < group.favorites?.length; j += 1) {
                    const fav = group.favorites[j];
                    if (fav.entity_type === 'campsite') {
                        if (fav.parent_id) {
                            docIds.add(`${fav.parent_id}_asset`);
                        }
                    } else if (fav.entity_type === 'campground') {
                        docIds.add(`${fav.entity_id}_asset`);
                    } else if (fav.entity_type === 'sitepass') {
                        // site passes don't have distinct search records in search results
                        docIds.add(`${fav.entity_id}_asset`);
                    } else if (fav.entity_type === 'recarea') {
                        docIds.add(`${fav.entity_id}_asset`);
                    } else if (fav.entity_type === 'facility') {
                        docIds.add(`${fav.entity_id}_asset`);
                    } else {
                        docIds.add(`${fav.entity_id}_${fav.entity_type}`);
                    }
                    if (j > 3) {
                        break;
                    }
                }
            }

            if (docIds.size === 0) {
                return;
            }
            const url = URLHelper.urlWithParams(`${process.env.API}/search/geo`, {
                fq: [...docIds].map((id) => `id:${id}`),
                size: 50,
            });
            const res = await axios.get(url);
            // image cdn urls
            const imageGroup: Record<string, string> = {};

            for (let i = 0; i < groupItem.length; i += 1) {
                const group = groupItem[i];
                for (let j = 0; j < group.favorites?.length; j += 1) {
                    const fav = group.favorites[j];
                    for (let k = 0; k < res?.data?.results?.length; k += 1) {
                        const item = res?.data?.results[k];
                        if (
                            (item.entity_id === fav.entity_id ||
                                item.entity_id === fav.parent_id) &&
                            item.preview_image_url
                        ) {
                            imageGroup[group.group_id] = item.preview_image_url;
                            break;
                        }
                    }
                }
            }
            setImages(imageGroup);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log('no preview image', error);
        }
    };

    // load the category groups of favorites if they exist
    const loadFavoritesGroups = async () => {
        setLoading(true);
        setErrors([]);
        try {
            const url = `${process.env.API}/trips/favorites/groups`;
            const res = await axios.get(url, {
                headers: { Authorization: getAuthHeader() },
            });
            const gr: FavoriteGroup[] = res?.data?.result || [];

            gr.sort((a, b) => {
                return a.group_name
                    ?.toLowerCase()
                    .localeCompare(b.group_name?.toLowerCase());
            });

            setGroups(gr);
            loadPreviewImages(gr);
        } catch {
            setErrors(['There were errors loading favorites']);
        }
        setLoading(false);
    };

    // collect ids of favorites and query search service for any records with preview image urls
    const loadFavoriteDetails = async () => {
        // generated facility id array with no duplicates
        const docIds = new Set();
        // generated facility id. Ex: '10073370_ticketfacility'
        const campsiteDocIds = new Set();
        if (entityType === 'campsite') {
            campsiteDocIds.add(`${entityId}_${entityType}`);
        } else if (entityType === 'campground') {
            docIds.add(`${entityId}_asset`);
        } else if (entityType === 'sitepass') {
            // site passes don't have distinct search records in search results
            docIds.add(`${entityId}_asset`);
        } else if (entityType === 'recarea') {
            docIds.add(`${entityId}_asset`);
        } else if (entityType === 'facility') {
            docIds.add(`${entityId}_asset`);
        } else {
            docIds.add(`${entityId}_${entityType}`);
        }

        const details: Record<string, FavoriteEntityDetails> = {};

        try {
            const url = URLHelper.urlWithParams(`${process.env.API}/search/geo`, {
                fq: [...docIds].map((id) => `id:${id}`),
                size: 500,
            });

            const res = await axios.get(url);
            // eslint-disable-next-line no-unused-expressions
            res?.data?.results?.forEach((item: FavoriteEntityDetails) => {
                details[`asset_${item.entity_id}`] = item;
            });
        } catch (error) {
            setErrors(['There were errors loading favorites', `${error}`]);
        }

        if (campsiteDocIds.size > 0) {
            try {
                // load campsite details
                const url = URLHelper.urlWithParams(
                    `${process.env.API}/search/campsites`,
                    {
                        fq: [...campsiteDocIds].map((id) => `id:${id}`),
                        size: 500,
                    }
                );
                const res = await axios.get(url);
                // eslint-disable-next-line no-unused-expressions
                res?.data?.campsites?.forEach((item: FavoriteEntityDetails) => {
                    details[`campsite_${item.campsite_id}`] = item;
                });
            } catch (error) {
                setErrors(['There were errors loading favorites', `${error}`]);
            }
        }
        setFavoritesDetails(details);
    };

    const hasNoCustomLists =
        groups.length === 1 && groups[0]?.group_name === 'All Favorites';

    React.useEffect(() => {
        setOpen(true);
        loadFavoritesGroups();
        loadFavoriteDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, entityType]);

    React.useEffect(() => {
        const favFoundItems: { [key: string]: boolean } = {};
        for (let i = 0; i < (groups || []).length; i += 1) {
            for (let j = 0; j < (groups[i]?.favorites || []).length; j += 1) {
                const found =
                    groups[i]?.favorites[j]?.entity_id === entityId &&
                    groups[i]?.favorites[j]?.entity_type === entityType;
                if (found) {
                    favFoundItems[groups[i].group_id] = found;
                }
            }
        }
        favFoundItems['all'] = true;
        setSelectedGroups(favFoundItems);
    }, [groups, entityId, entityType]);

    const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const hasName = Boolean(event.target.value?.trim());
        setNewGroupName(event.target.value);
        setEdited(hasName);
        setSelectedGroups({ ...selectedGroups, new: hasName });
    };

    const buildFavoritesGroupsToSave = () => {
        const { toSave, groupsCopy } = computeFavoritesGroupsToSave({
            groups,
            selectedGroups,
            newGroupName,
            entityId,
            entityType,
            parentId,
            parentType,
        });
        setGroups(groupsCopy);
        return toSave;
    };

    const save = async (groupsToSave: Partial<FavoriteGroup>[]) => {
        if (!favoritesDetails) {
            return;
        }

        setLoading(true);
        setErrors([]);
        try {
            const url = `${process.env.API}/trips/favorites/group`;
            for (let i = 0; i < groupsToSave.length; i += 1) {
                const group = groupsToSave[i];
                /* eslint-disable no-await-in-loop */
                await axios.post(url, group, {
                    headers: { Authorization: getAuthHeader() },
                });
            }
            const details: FavoriteEntityDetails =
                entityType === 'campsite'
                    ? favoritesDetails[`campsite_${entityId}`]
                    : favoritesDetails[`asset_${entityId}`];
            const message = `"${details?.name}"  ${
                mode === 'edit'
                    ? isRemoveAllModalOpen
                        ? 'removed from'
                        : 'saved in'
                    : 'added to'
            } Favorites.`;

            showToast(message, { type: 'info' });
            setOpen(false);
            if (onSave) {
                onSave();
            }
        } catch {
            setLoading(false);
            setErrors(['There were errors saving favorites']);
        }
        setLoading(false);
    };

    const removeFromFavorites = async () => {
        // Filter the groups that contain the favorite item being removed
        const groupsToUpdate = groups.filter((group) => {
            const filtered = group.favorites?.filter(
                (favorite) =>
                    favorite.entity_id === entityId && favorite.entity_type === entityType
            );
            return filtered?.length > 0;
        });
        // Update the favorite item to be inactive
        const updatedGroups = groupsToUpdate.map((g) => {
            const group = { ...g };
            const filtered = group.favorites?.map((f) => {
                const fav = { ...f };
                if (fav.entity_id === entityId && fav.entity_type === entityType) {
                    fav.is_active = false;
                }
                return fav;
            });
            group.favorites = filtered;
            return group;
        });
        // Save updated groups
        await save(updatedGroups);
        setIsRemoveAllModalOpen(false);
    };

    const dismissModal = () => {
        setOpen(false);
        if (onCancel) {
            onCancel();
        }
    };

    const favGaTrackingId = '199951850105';

    const saveGaTrackingValue = React.useMemo(() => {
        const { toSave } = computeFavoritesGroupsToSave({
            groups,
            selectedGroups,
            newGroupName,
            entityId,
            entityType,
            parentId,
            parentType,
        });
        return formatFavoriteSaveGaTrackingValue(
            deriveFavoriteSaveGaFlagsFromToSave(toSave, entityId, entityType),
            mode
        );
    }, [
        groups,
        selectedGroups,
        newGroupName,
        entityId,
        entityType,
        parentId,
        parentType,
        mode,
    ]);

    const renderGroupsList = () => {
        if (loading) {
            return null;
        }
        return (
            <>
                <FlexRow alignItems="center" hasGutters={false} className="group-item">
                    <FlexCol xs="variable">
                        <div className="heart-icon-block">
                            <Icon iconName="favorite" />
                        </div>
                    </FlexCol>
                    <FlexCol xs="auto">
                        <Heading headingLevel={2} appearance="h6">
                            All Favorites
                        </Heading>
                    </FlexCol>
                    <FlexCol xs="variable">
                        {mode === 'edit' ? (
                            <Button
                                appearance="tertiary"
                                gaTrackingId={favGaTrackingId}
                                gaTrackingValue="Remove Item from All Lists Button"
                                onClick={() => {
                                    setIsRemoveAllModalOpen(true);
                                }}
                            >
                                Remove Item from All Lists
                            </Button>
                        ) : (
                            <Text fontWeight="bold">
                                Automatically Added to All Favorites
                            </Text>
                        )}
                    </FlexCol>
                </FlexRow>
                {hasNoCustomLists && (
                    <Alert type="info" className="mt-2 mb-6">
                        By default, all favorites are automatically added to the All
                        Favorites list. You can also create a custom list by clicking on
                        the &ldquo;Create New List&rdquo; button below to add this item to
                        a new custom list as well.
                    </Alert>
                )}
                {groups.map((l, i) => {
                    const checked = selectedGroups[l.group_id] || false;
                    if (l.group_name === 'All Favorites') {
                        // skip default favorites group
                        return null;
                    }
                    return (
                        <div key={`fav_${i}_${l.group_id}`} className="group-item">
                            <Divider />
                            <FlexRow alignItems="center" hasGutters={false}>
                                <FlexCol xs="variable">
                                    <FauxImage
                                        className="faux-image-block"
                                        src={images[l.group_id]}
                                    />
                                </FlexCol>
                                <FlexCol xs="auto">
                                    <Heading headingLevel={2} appearance="h6">
                                        {l.group_name}
                                    </Heading>
                                </FlexCol>
                                <FlexCol xs="variable">
                                    <Checkbox
                                        key={`l-ch-${l.group_id}`}
                                        id={`l_id-${l.group_id}`}
                                        className="mr-1"
                                        value={l.group_id}
                                        label="Add to List"
                                        aria-label={`Add to ${l.group_name} list`}
                                        isChecked={checked}
                                        onChange={() => {
                                            const selectedItem = { ...selectedGroups };
                                            selectedItem[l.group_id] =
                                                !selectedItem[l.group_id];
                                            setSelectedGroups(selectedItem);
                                            setEdited(true);
                                        }}
                                    />
                                </FlexCol>
                            </FlexRow>
                        </div>
                    );
                })}
            </>
        );
    };

    const renderCreateNewList = () => {
        return (
            <FlexRow>
                <FlexCol className={'mb-2'}>
                    <TextField
                        id="new-list-name"
                        label="enter list name"
                        isLabelVisible={false}
                        placeholder={'Enter List Name'}
                        onChange={onTitleChange}
                        value={newGroupName}
                        shouldFocusOnMount
                        hasClearButton
                    />
                </FlexCol>
            </FlexRow>
        );
    };

    const renderModalActions = () => {
        return (
            <ModalActions>
                {!isCreateNewList && (
                    <div className="create-new-list-btn">
                        <Button
                            appearance={'link'}
                            iconBeforeElement={<Icon iconName="add-circle" />}
                            gaTrackingId={favGaTrackingId}
                            gaTrackingValue="Create New List Button"
                            onClick={() => {
                                setCreateNewList(true);
                            }}
                        >
                            Create New List
                        </Button>
                    </div>
                )}
                <ButtonGroup>
                    <Button
                        appearance={'tertiary'}
                        gaTrackingId={favGaTrackingId}
                        gaTrackingValue="Cancel Button"
                        onClick={() => {
                            setOpen(false);
                            if (onCancel) {
                                onCancel();
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        gaTrackingId={favGaTrackingId}
                        gaTrackingValue={saveGaTrackingValue}
                        onClick={() => {
                            save(buildFavoritesGroupsToSave());
                        }}
                        isDisabled={
                            (mode === 'add' &&
                                Object.keys(selectedGroups).length === 0) ||
                            (mode === 'edit' && !isEdited)
                        }
                    >
                        Save
                    </Button>
                </ButtonGroup>
            </ModalActions>
        );
    };

    let title: string;
    switch (true) {
        case isCreateNewList:
            title = 'Create New List';
            break;
        case mode === 'add':
            title = 'Add to Favorites';
            break;
        case mode === 'edit':
            title = 'Edit Favorites';
            break;
        default:
            title = '';
    }

    if (isRemoveAllModalOpen) {
        return (
            <FavoriteHeartRemoveAllModal
                isRemoveAllModalOpen={isRemoveAllModalOpen}
                setIsRemoveAllModalOpen={setIsRemoveAllModalOpen}
                dismissModal={dismissModal}
                loading={loading}
                removeFromFavorites={removeFromFavorites}
            />
        );
    }

    return (
        <StyledModal
            heading={title}
            size="md"
            isOpen={isOpen}
            overlayClassName="sui-favorites-modal-wrap"
            onRequestClose={dismissModal}
            shouldActionsStickInMobile
        >
            {loading && <Spinner />}

            {errors.length > 0 && (
                <Alert type="error" heading={'There were errors:'} className="mb-3">
                    <ul>
                        {errors.map((err, i) => {
                            return <li key={`err-${i}`}>{`${err}`}</li>;
                        })}
                    </ul>
                </Alert>
            )}

            {!isCreateNewList && renderGroupsList()}
            {isCreateNewList && renderCreateNewList()}
            {renderModalActions()}
        </StyledModal>
    );
}

// cSpell:ignore sitepass, recarea, 10073370_ticketfacility
